// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type {
  Coding,
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireItemAnswerOption,
  QuestionnaireItemEnableWhen,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from '@medplum/fhirtypes';

export type FormValues = Record<string, unknown>;

/**
 * Build a FHIR QuestionnaireResponse from a Mantine-form values map keyed by
 * Questionnaire item linkId. The output matches the shape produced by
 * `@medplum/react`'s QuestionnaireForm, so downstream bots that call
 * `getQuestionnaireAnswers(response)` consume it unchanged.
 *
 * Rules
 * - Skips items whose `enableWhen` is not satisfied by the provided values.
 * - For `choice` items, infers `valueCoding` vs `valueString` from the matched
 *   `answerOption` (falls back to `valueString` when no option matches).
 * - For multi-select choice (array value), emits one answer entry per element.
 * - Empty / `undefined` / empty-string / empty-array values are omitted.
 */
export function buildQuestionnaireResponse(questionnaire: Questionnaire, values: FormValues): QuestionnaireResponse {
  const responseItems: QuestionnaireResponseItem[] = [];
  for (const item of questionnaire.item ?? []) {
    if (!isItemEnabled(item, values)) {
      continue;
    }
    const responseItem = buildResponseItem(item, values);
    if (responseItem) {
      responseItems.push(responseItem);
    }
  }
  return {
    resourceType: 'QuestionnaireResponse',
    status: 'completed',
    questionnaire: questionnaire.url ?? (questionnaire.id ? `Questionnaire/${questionnaire.id}` : undefined),
    item: responseItems,
  };
}

function buildResponseItem(item: QuestionnaireItem, values: FormValues): QuestionnaireResponseItem | undefined {
  const linkId = item.linkId;
  if (!linkId) {
    return undefined;
  }
  const value = values[linkId];
  const answers = toAnswers(item, value);
  const nestedItems: QuestionnaireResponseItem[] = [];
  for (const child of item.item ?? []) {
    if (!isItemEnabled(child, values)) {
      continue;
    }
    const childResponse = buildResponseItem(child, values);
    if (childResponse) {
      nestedItems.push(childResponse);
    }
  }
  if (answers.length === 0 && nestedItems.length === 0) {
    return undefined;
  }
  return {
    linkId,
    ...(item.text ? { text: item.text } : {}),
    ...(answers.length > 0 ? { answer: answers } : {}),
    ...(nestedItems.length > 0 ? { item: nestedItems } : {}),
  };
}

function toAnswers(item: QuestionnaireItem, value: unknown): QuestionnaireResponseItemAnswer[] {
  if (value === undefined || value === null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((entry) => toAnswers(item, entry));
  }
  if (typeof value === 'string' && value.trim() === '') {
    return [];
  }
  const answer = toSingleAnswer(item, value);
  return answer ? [answer] : [];
}

function toSingleAnswer(item: QuestionnaireItem, value: unknown): QuestionnaireResponseItemAnswer | undefined {
  switch (item.type) {
    case 'boolean':
      return { valueBoolean: Boolean(value) };
    case 'integer':
      return { valueInteger: typeof value === 'number' ? value : Number(value) };
    case 'decimal':
      return { valueDecimal: typeof value === 'number' ? value : Number(value) };
    case 'date':
      return { valueDate: String(value) };
    case 'dateTime':
      return { valueDateTime: String(value) };
    case 'time':
      return { valueTime: String(value) };
    case 'choice':
    case 'open-choice': {
      const matched = matchAnswerOption(item.answerOption, value);
      if (matched?.valueCoding) {
        return { valueCoding: matched.valueCoding };
      }
      if (matched?.valueString !== undefined) {
        return { valueString: matched.valueString };
      }
      // open-choice free text / unknown option
      return { valueString: String(value) };
    }
    case 'string':
    case 'text':
    case 'url':
    default:
      return { valueString: String(value) };
  }
}

function matchAnswerOption(
  options: QuestionnaireItemAnswerOption[] | undefined,
  value: unknown
): QuestionnaireItemAnswerOption | undefined {
  if (!options) {
    return undefined;
  }
  for (const option of options) {
    if (option.valueString !== undefined && option.valueString === value) {
      return option;
    }
    if (option.valueCoding && isCodingMatch(option.valueCoding, value)) {
      return option;
    }
  }
  return undefined;
}

function isCodingMatch(coding: Coding, value: unknown): boolean {
  if (typeof value === 'string') {
    return coding.code === value || coding.display === value;
  }
  if (value && typeof value === 'object') {
    const v = value as Partial<Coding>;
    if (v.code && coding.code === v.code) {
      return true;
    }
    if (v.display && coding.display === v.display) {
      return true;
    }
    return false;
  }
  return false;
}

function isItemEnabled(item: QuestionnaireItem, values: FormValues): boolean {
  const conditions = item.enableWhen;
  if (!conditions || conditions.length === 0) {
    return true;
  }
  const behavior = item.enableBehavior ?? 'all';
  const results = conditions.map((cond) => evaluateEnableWhen(cond, values));
  return behavior === 'any' ? results.some(Boolean) : results.every(Boolean);
}

function evaluateEnableWhen(cond: QuestionnaireItemEnableWhen, values: FormValues): boolean {
  const actual = values[cond.question ?? ''];
  if (cond.answerBoolean !== undefined) {
    const left = Boolean(actual);
    return applyOperator(cond.operator, left, cond.answerBoolean);
  }
  if (cond.answerString !== undefined) {
    return applyOperator(cond.operator, actual, cond.answerString);
  }
  if (cond.answerCoding) {
    if (typeof actual === 'string') {
      return applyOperator(cond.operator, actual, cond.answerCoding.code);
    }
    if (actual && typeof actual === 'object') {
      const a = actual as Partial<Coding>;
      return applyOperator(cond.operator, a.code, cond.answerCoding.code);
    }
    return cond.operator === '!=';
  }
  return true;
}

/**
 * Inverse of buildQuestionnaireResponse — flatten a QuestionnaireResponse into a
 * linkId-keyed value map suitable for seeding form `initialValues` when a user
 * navigates back to a previously completed step.
 */
export function questionnaireResponseToValues(response: QuestionnaireResponse | undefined | null): FormValues {
  const out: FormValues = {};
  if (!response?.item) {
    return out;
  }
  const visit = (items: QuestionnaireResponseItem[] | undefined): void => {
    for (const item of items ?? []) {
      if (!item.linkId) {
        continue;
      }
      if (item.answer && item.answer.length > 0) {
        const answers = item.answer.map(extractAnswerValue);
        out[item.linkId] = answers.length === 1 ? answers[0] : answers;
      }
      if (item.item) {
        visit(item.item);
      }
    }
  };
  visit(response.item);
  return out;
}

function extractAnswerValue(answer: QuestionnaireResponseItemAnswer): unknown {
  if (answer.valueBoolean !== undefined) {
    return answer.valueBoolean;
  }
  if (answer.valueInteger !== undefined) {
    return answer.valueInteger;
  }
  if (answer.valueDecimal !== undefined) {
    return answer.valueDecimal;
  }
  if (answer.valueString !== undefined) {
    return answer.valueString;
  }
  if (answer.valueDate !== undefined) {
    return answer.valueDate;
  }
  if (answer.valueDateTime !== undefined) {
    return answer.valueDateTime;
  }
  if (answer.valueTime !== undefined) {
    return answer.valueTime;
  }
  if (answer.valueCoding) {
    return answer.valueCoding.code ?? answer.valueCoding.display;
  }
  return undefined;
}

function applyOperator(operator: string | undefined, actual: unknown, expected: unknown): boolean {
  switch (operator) {
    case '!=':
      return actual !== expected;
    case '>':
      return Number(actual) > Number(expected);
    case '<':
      return Number(actual) < Number(expected);
    case '>=':
      return Number(actual) >= Number(expected);
    case '<=':
      return Number(actual) <= Number(expected);
    case 'exists': {
      const wantsExists = expected === true || expected === 'true';
      return wantsExists ? actual !== undefined && actual !== null && actual !== '' : !actual;
    }
    case '=':
    default:
      return actual === expected;
  }
}
